import React, { createContext, useContext, useReducer, ReactNode } from 'react';

// --- TYPES ---
export interface User {
  id: number;
  phone_number: string;
  full_name: string;
  role: 'user' | 'admin' | 'super_admin';
  is_admin: boolean;
  is_active: boolean;
  created_at: string;
}

export interface Provider {
  id: number;
  user_id: number;
  business_name: string;
  service_category: string;
  description: string;
  contact_phone: string;
  whatsapp_number: string;
  email: string;
  city: string;
  latitude?: number;
  longitude?: number;
  experience: string;
  service_mode: string;
  is_looking_for_job: boolean;
  availability_status: 'Available' | 'Busy' | 'Disabled';
  is_active: boolean;
  created_at: string;
  view_count?: number;
  phone_click_count?: number;
  whatsapp_click_count?: number;
  profile_image_url?: string;

  // Relations
  total_ratings?: number;
  ratings?: Rating[];
  user?: User;
  profile?: ProviderProfile | null;
  payment_details?: ProviderPaymentDetails | null;
  gallery_images?: ProviderGalleryImage[];

  // New / Search Fields
  trust_count?: number;
  trusted_by?: Array<{ name: string; id: number }>;
  products?: Product[];
  is_verified?: boolean;
  subscription_status?: 'free' | 'active' | 'expired';
  subscription_end_date?: string;
  is_public?: boolean;

  // ✅ ADDED: This comes from the backend geospatial search
  distance_km?: number;
}

export interface ProviderProfile {
  about?: string;
  services_summary?: string;
  instagram_url?: string;
  facebook_url?: string;
  youtube_url?: string;
  website_url?: string;
  intro_video_url?: string;
  business_logo_url?: string;
  cover_image_url?: string;
}

export interface Product {
  id: number;
  provider_id: number;
  name: string;
  description: string;
  price: string;
  offer_price?: string;
  product_link?: string;
  image_url: string;
  is_active: boolean;
  created_at: string;
}

export interface ProviderPaymentDetails {
  upi_id?: string;
  qr_code_url?: string;
  account_holder_name?: string;
  bank_name?: string;
  payment_terms?: string;
  charge_amount?: string;
  account_number?: string;
  ifsc_code?: string;
}

export interface ProviderGalleryImage {
  id: number;
  image_url: string;
  caption?: string;
}

export interface Rating {
  id: number;
  provider_id: number;
  reviewer_user_id: number;
  reviewer_name: string;
  rating_value: 'p' | 'p+' | 'p++' | 'p*';
  review_text: string;
  is_active: boolean;
  created_at: string;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isProvider: boolean;
  provider: Provider | null;
}

export interface AppState {
  auth: AuthState;
  providers: Provider[];
  loading: boolean;
  error: string | null;
}

// --- INITIAL STATE ---
const initialState: AppState = {
  auth: {
    user: null,
    token: null,
    isAuthenticated: false,
    isProvider: false,
    provider: null,
  },
  providers: [],
  loading: false,
  error: null,
};

// --- ACTIONS ---
type Action =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string; provider?: Provider } }
  | { type: 'LOGOUT' }
  | { type: 'SET_PROVIDERS'; payload: Provider[] }
  | { type: 'SET_PROFILE'; payload: { user: User; provider?: Provider } }
  | { type: 'UPDATE_PROVIDER'; payload: Provider }
  | { type: 'SET_PROVIDER_STATUS'; payload: Provider };

// --- REDUCER ---
function reducer(state: AppState, action: Action): AppState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'LOGIN_SUCCESS':
      sessionStorage.setItem('cardbox_token', action.payload.token);
      return {
        ...state,
        auth: {
          user: action.payload.user,
          token: action.payload.token,
          isAuthenticated: true,
          isProvider: !!action.payload.provider,
          provider: action.payload.provider || null,
        },
      };
    case 'LOGOUT':
      sessionStorage.removeItem('cardbox_token');
      return {
        ...state,
        auth: {
          user: null,
          token: null,
          isAuthenticated: false,
          isProvider: false,
          provider: null,
        },
      };
    case 'SET_PROVIDERS':
      return { ...state, providers: action.payload };
    case 'SET_PROFILE':
      return {
        ...state,
        auth: {
          ...state.auth,
          user: action.payload.user,
          isProvider: !!action.payload.provider,
          provider: action.payload.provider || null,
        },
      };
    case 'UPDATE_PROVIDER':
    case 'SET_PROVIDER_STATUS':
      return {
        ...state,
        auth: {
          ...state.auth,
          provider: action.payload,
        },
      };
    default:
      return state;
  }
}

// --- CONTEXT ---
interface StoreContextType {
  state: AppState;
  dispatch: React.Dispatch<Action>;
}

const StoreContext = createContext<StoreContextType | undefined>(undefined);

export function StoreProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  React.useEffect(() => {
    // 1. Check for URL Parameter (Magic Link from Admin)
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('impersonate_token');

    if (urlToken) {
      // ✅ Found token in URL! Use this for the new tab.
      sessionStorage.setItem('cardbox_token', urlToken);
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: null as any, // User will be fetched by your app layout/initializer
          token: urlToken,
          provider: undefined
        }
      });

      // Clean the URL so the token isn't visible to the user
      window.history.replaceState({}, document.title, window.location.pathname);
      return; 
    }

    // 2. Fallback: Check existing Session Storage
    const storageToken = sessionStorage.getItem('cardbox_token');
    if (storageToken) {
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: null as any,
          token: storageToken,
          provider: undefined
        }
      });
    }
  }, []);

  return React.createElement(
    StoreContext.Provider,
    { value: { state, dispatch } },
    children
  );
}

// --- HOOK ---
export function useStore() {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
}

// --- SELECTORS ---
export const getters = {
  isAuthenticated: (state: AppState) => state.auth.isAuthenticated,
  currentUser: (state: AppState) => state.auth.user,
  isAdmin: (state: AppState) => state.auth.user?.is_admin || false,
  isProvider: (state: AppState) => state.auth.isProvider,
  currentProvider: (state: AppState) => state.auth.provider,
  token: (state: AppState) => state.auth.token,
};